import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Icon from "../Icon/Icon";
import { ChevronDownSolid, ChevronUpSolid } from "../../assets/icons";

const StyledButton = styled.button<{
  boldStyle?: boolean;
  customText?: boolean;
  colour?: string;
}>`
  text-decoration: underline;
  font-weight: ${({ boldStyle }) => (boldStyle ? "bold" : "normal")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${({ customText }) =>
    customText ? "center" : "space-between"};
  width: 100%;
  border: none;
  background: none;
  padding: 3px;
  color: ${({ colour }) => colour ?? "#202227"};
  font-family: inherit;
  font-size: 1em;
  transition: border-color 0.25s;
`;

const ChevronIcon = styled(Icon)`
  font-size: 18px;
`;

export type LinkButtonProps = {
  /**
   * The children prop represents the text content of the button.
   * Default text for button is More available times and on click it changes to Show less
   * If provided, it will override the default text content.
   */
  children?: string;
  boldStyle?: boolean;
  onClick: (loadMore: boolean) => void;
  textColour?: string;
  availableTimesCount?: number;
  dataId?: string;
};

export const LinkButton: React.FC<LinkButtonProps> = ({
  children,
  boldStyle,
  onClick,
  textColour,
  availableTimesCount = 0,
  dataId: dataIdProp,
}) => {
  const { t } = useTranslation();
  /**
   * The isExpanded state tracks whether parent component should be expanded or collapsed based on button presses and cases.
   * When true, it indicates that additional content is displayed.
   * When false, it indicates that the additional content is hidden.
   */
  const [isExpanded, setIsExpanded] = useState(false);

  const handleButtonClick = () => {
    setIsExpanded(!isExpanded);
    onClick(children ? true : !isExpanded);
  };

  const arrowIcon = isExpanded ? ChevronUpSolid : ChevronDownSolid;
  const buttonText = children
    ? children
    : isExpanded
      ? t("showLess")
      : availableTimesCount !== 1
        ? `${availableTimesCount} ${t("moreAvailableTimes")}`
        : `${availableTimesCount} ${t("moreAvailableTime")}`;
  const dataId =
    dataIdProp || isExpanded ? "show-less-button" : "show-more-button";

  return (
    <StyledButton
      type="button"
      onClick={handleButtonClick}
      boldStyle={boldStyle}
      customText={!!children}
      colour={textColour}
      data-id={dataId}
    >
      {buttonText}
      {!children && <ChevronIcon icon={arrowIcon} />}
    </StyledButton>
  );
};

export default LinkButton;
